import React, { useState, useEffect } from 'react';
import { Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Organizations from './pages/organizations/organizations';
import OranizationDashboard from './pages/organizations/organizationDashboard';
import OrganizationPentests from './pages/organizations/organizationPentests';
import OrganizationScheduling from './pages/organizations/organizationScheduling';
import AdminPanel from './pages/admin/admin';
import TopNav from './pages/sidebar/TopNav';
import ApiDocs from './pages/swagger/swagger';
import Login from './pages/login/Login';
import Register from './pages/register/register';
import ForgotPassword from './pages/forgotPassword/forgotPassword';
import Onboarding from './pages/firstLoginOnboarding/onboarding';
import Pentests from './pages/pentests/pentests';
import PentestAnalytics from './pages/pentests/pentestAnalytics';
import PentestDashboard from './pages/pentest/dashboard';
import AttackGraph from "./pages/pentest/attackGraph";
import Copilot from './pages/copilot/Copilot';
import RemoteMachines from './pages/copilot/RemoteMachines';
import ReportsHub from './pages/reports/ReportsHub';
import ManualReportsPage from './pages/reports/manual/ManualReportsPage';
import ManualReportWorkspacePage from './pages/reports/manual/ManualReportWorkspacePage';
import Tools from './pages/pentest/tools';
import Exploits from './pages/pentest/exploits';
import Cves from './pages/pentest/cves';
import Assets from './pages/pentest/assets';
import Urls from './pages/pentest/urls';
import ExternalAttackHistory from './pages/pentest/attackHistory';
import PentestExternalThreatModel from './pages/pentest/threatModel';
import ReportsPage from './pages/pentest/reports/reports';
import ExecutiveReport from './pages/pentest/reports/executiveReport';
import AttestationReport from './pages/pentest/reports/attestationReport';
import ExternalReconReport from './pages/pentest/reports/reconReport';
import Integrations from './pages/pentest/integrations';
import PentestScope from './pages/pentest/scope';
import PentestNotes from './pages/pentest/notes';
import CreatePentest from './pages/pentests/createPentest';
import CreateApiPentest from './pages/pentests/createAPIPentest';
import CreateExternalPentest from './pages/pentests/createExternalPentest';
import CreateVishingPentest from './pages/pentests/createVishingPentest';
import CreateDarkWebPentest from './pages/pentests/createDarkWebPentest';
import CreateOSINTPentest from './pages/pentests/createOSINTPentest';
import PentestSettingsPage from './pages/pentest/settings';
import VishingEmployees from './pages/pentestVishing/employees';
import VishingCalls from './pages/pentestVishing/calls';
import VishingFindings from './pages/pentestVishing/findings';
import VishingDashboard from './pages/pentestVishing/dashboard';
import VishingExecutiveReport from './pages/pentestVishing/reports/executiveReport';
import VishingAttestationReport from './pages/pentestVishing/reports/attestationReport';
import VishingReportsPage from './pages/pentestVishing/reports/reports';
import DarkWebDashboard from './pages/pentestDarkweb/dashboard';
import DarkWebFindings from './pages/pentestDarkweb/findings';
import DarkWebReportsPage from './pages/pentestDarkweb/reports';
import ExecutiveSummaryPageDarkweb from './pages/pentestDarkweb/executiveReportDarkweb';
import AttestationLetterDarkweb from './pages/pentestDarkweb/attestationReport';
import MembershipPopup from './components/MembershipPopup';
import PaymentSuccess from './pages/stripe/paymentSuccess';
import ApiPentestDashboard from './pages/pentestApi/dashboard';
import ApiPentestScope from './pages/pentestApi/scope';
import PentestApiAssets from './pages/pentestApi/assets';
import PentestApiExploits from './pages/pentestApi/exploits';
import PentestApiThreatModel from './pages/pentestApi/threatModel';
import ReportsPageAPIPentest from './pages/pentestApi/reports/reports';
import AttestationLetterAPIPentest from './pages/pentestApi/reports/attestationReport';
import ExecutiveReportAPIPentest from './pages/pentestApi/reports/executiveReport';
import Team from './pages/team/team';
import CreateWebPentest from './pages/pentests/createWebPentest';
import WebPentestDashboard from './pages/pentestWeb/dashboard';
import WebPentestScope from './pages/pentestWeb/scope';
import PentestWebAssets from './pages/pentestWeb/assets';
import PentestWebExploits from './pages/pentestWeb/exploits';
import PentestWebThreatModel from './pages/pentestWeb/threatModel';
import ReportsPageWebPentest from './pages/pentestWeb/reports/reports';
import AttestationLetterWebPentest from './pages/pentestWeb/reports/attestationReport';
import ExecutiveReportWebPentest from './pages/pentestWeb/reports/executiveReport';
import ReconReportWebPentest from './pages/pentestWeb/reports/reconReport';
import Intel from './pages/intel/Intel';
import OsintDashboard from './pages/pentestOsint/dashboard';
import OsintCompany from './pages/pentestOsint/company';
import OsintEmployees from './pages/pentestOsint/employees';
import OsintInfrastructure from './pages/pentestOsint/infrastructure';
import OsintMediaSocial from './pages/pentestOsint/mediaSocial';
import BillingPage from './pages/billing/BillingPage';
import SettingsLayout from './pages/settings/SettingsLayout';
import AccountSettings from './pages/settings/AccountSettings';
import { CreditsProvider } from './context/CreditsContext';
import { AUTH_TOKEN_CHANGED_EVENT } from './services/creditsApi';

const PUBLIC_SHELL_PAGES = ['create-account', 'forgot-password', 'onboarding', '', 'api-docs-public'];

// A simple wrapper for all protected pages.
// It checks membership, and if the user isn't a "beta" member,
// it overrides the intended content with the membership page.
function ProtectedRoute({ element, membership, userEmail, onMembershipUpdated }) {
  if (membership !== 'beta' && membership !== 'admin') {
    return (
      <MembershipPopup
        userEmail={userEmail}
        onMembershipUpdated={onMembershipUpdated}
      />
    );
  }
  return element;
}

function App() {
  const [membership, setMembership] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname.replace('/', '');
  const showTopNav = !PUBLIC_SHELL_PAGES.includes(currentPage);

  // Function to get the token from sessionStorage
  const getToken = () => sessionStorage.getItem('token');

  // Decode the JWT token to extract its payload
  const decodeToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Invalid token:', error);
      return null;
    }
  };

  useEffect(() => {
    const handleTokenUpdate = () => {
      const token = getToken();
      if (token) {
        const decodedToken = decodeToken(token);
        if (decodedToken) {
          setUserEmail(decodedToken.username);
          setMembership(decodedToken.membership);
        }
      }
    };

    // Handle token when the page loads or updates
    handleTokenUpdate();

    // Listen for storage changes to detect when the token is set/updated
    window.addEventListener('storage', handleTokenUpdate);
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, handleTokenUpdate);
    return () => {
      window.removeEventListener('storage', handleTokenUpdate);
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, handleTokenUpdate);
    };
  }, [navigate]);

  useEffect(() => {
    // Redirect unauthenticated users to login
    if (
      !getToken() &&
      !PUBLIC_SHELL_PAGES.includes(currentPage)
    ) {
      navigate('/');
    }
  }, [currentPage, navigate]);

  return (
    <CreditsProvider>
      <div className={`App app-shell ${showTopNav ? 'has-top-nav' : 'no-top-nav'}`}>
        {/* Show global navigation on protected app pages only. */}
        {showTopNav && <TopNav />}

        <main className="app-main">
          <Routes>
          {/* Public Routes */}
          <Route exact path="/" element={<Login />} />
          <Route path="/create-account" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/api-docs-public" element={<ApiDocs />} />

          {/* Admin Route  protected membership must be admin not beta*/}
          <Route
            exact
            path="/admin"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('admin')}
                element={<AdminPanel />}
              />
            }
          />

          {/* Protected Routes (overridden by membership page if membership is not "beta") */}

          {/*Intel Page*/}
          <Route
            exact
            path="/intel"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Intel />}
              />
            }
          />

          {/*Organization ROutes */}
          <Route
            exact
            path="/organizations"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Organizations />}
              />
            }
          />
          <Route
            exact
            path="/organization/dashboard/:id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OranizationDashboard />}
              />
            }
          />
          <Route
            exact
            path="/organization/pentests/:id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OrganizationPentests />}
              />
            }
          />
          <Route
            exact
            path="/organization/schedule/:id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OrganizationScheduling />}
              />
            }
          />


          <Route
            exact
            path="/api-docs"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ApiDocs />}
              />
            }
          />
          <Route
            exact
            path="/pentests"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Pentests />}
              />
            }
          />
          <Route 
            exact
            path="/pentest/attack_history/:pentest_id" 
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ExternalAttackHistory />}
              />
            } 
          />
          <Route
            exact
            path="/pentest/analytics"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestAnalytics />}
              />
            }
          />
          <Route
            exact
            path="/copilot"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Copilot />}
              />
            }
          />
          <Route
            exact
            path="/copilot/remote-machines"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<RemoteMachines />}
              />
            }
          />
          <Route
            exact
            path="/reports"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ReportsHub />}
              />
            }
          />
          <Route
            exact
            path="/reports/manual"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ManualReportsPage />}
              />
            }
          />
          <Route
            exact
            path="/reports/manual/:reportId"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ManualReportWorkspacePage />}
              />
            }
          />
          <Route
            exact
            path="/pentests/create"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreatePentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/create/external"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreateExternalPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/create/vishing"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreateVishingPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/create/darkweb"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreateDarkWebPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/create/osint"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreateOSINTPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/create/api"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreateApiPentest />}
              />
            } 
          /> 
          <Route
            exact
            path="/pentest/dashboard/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestDashboard />}
              />
            }
          />
          <Route
            exact
            path="/pentest/threat_model/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestExternalThreatModel />}
              />
            }
          />
          <Route
            exact
            path="/pentest/attack-graph/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<AttackGraph />}
              />
            }
          />
          <Route
            exact
            path="/pentest/scope/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestScope />}
              />
            }
          />
          <Route
            exact
            path="/pentest/tools/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Tools />}
              />
            }
          />
          <Route
            exact
            path="/pentest/assets/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Assets />}
              />
            }
          />
          <Route
            exact
            path="/pentest/urls/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Urls />}
              />
            }
          />
          <Route
            exact
            path="/pentest/findings/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Exploits />}
              />
            }
          />
          <Route
            exact
            path="/pentest/cves/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Cves />}
              />
            }
          />
          <Route
            exact
            path="/pentest/report/pentest/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ReportsPage />}
              />
            }
          />
          <Route
            exact
            path="/pentest/report/executive/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ExecutiveReport />}
              />
            }
          />
          <Route
            exact
            path="/pentest/report/recon/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ExternalReconReport />}
              />
            }
          />
          <Route
            exact
            path="/pentest/report/attestation/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<AttestationReport />}
              />
            }
          />
          <Route
            exact
            path="/pentest/integrations/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<Integrations />}
              />
            }
          />
          <Route
            exact
            path="/pentest/notes/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestNotes />}
              />
            }
          />
          <Route
            exact
            path="/pentest/settings/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestSettingsPage />}
              />
            }
          />
          <Route
            exact
            path="/pentest/vishing/employees/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingEmployees />}
              />
            }
          />
          <Route
            exact
            path="/pentest/vishing/calls/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingCalls />}
              />
            }
          />
          <Route
            exact
            path="/pentest/vishing/findings/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingFindings />}
              />
            }
          />
          <Route
            exact
            path="/pentest/vishing/dashboard/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingDashboard />}
              />
            }
          />
          <Route
            exact
            path="/pentest/vishing/report/executive/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingExecutiveReport />}
              />
            }
          />
          <Route
            exact
            path="/pentest/vishing/report/attestation/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingAttestationReport />}
              />
            }
          />

          <Route
            exact
            path="/pentest/vishing/report/pentest/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<VishingReportsPage />}
              />
            }
          />
          <Route
            exact
            path="/pentest/darkweb/dashboard/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<DarkWebDashboard />}
              />
            }
          />
          <Route
            exact
            path="/pentest/darkweb/findings/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<DarkWebFindings />}
              />
            }
          />
          <Route
            exact
            path="/pentest/darkweb/report/pentest/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<DarkWebReportsPage />}
              />
            }
          />
          <Route
            exact
            path="/pentest/darkweb/report/executive/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ExecutiveSummaryPageDarkweb minimal={false} />}
              />
            }
          />
          <Route
            exact
            path="/pentest/darkweb/report/attestation/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<AttestationLetterDarkweb />}
              />
            }
          />

          {/* OSINT Routes */}
          <Route
            exact
            path="/pentest/osint/dashboard/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OsintDashboard />}
              />
            }
          />
          <Route
            exact
            path="/pentest/osint/company/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OsintCompany />}
              />
            }
          />
          <Route
            exact
            path="/pentest/osint/employees/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OsintEmployees />}
              />
            }
          />
          <Route
            exact
            path="/pentest/osint/infrastructure/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OsintInfrastructure />}
              />
            }
          />
          <Route
            exact
            path="/pentest/osint/business/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OsintCompany />}
              />
            }
          />
          <Route
            exact
            path="/pentest/osint/media/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<OsintMediaSocial />}
              />
            }
          />

          {/* API Pentest Routes */}
          <Route
            exact
            path="/pentest/api/dashboard/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ApiPentestDashboard />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/scope/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ApiPentestScope />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/assets/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestApiAssets />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/exploits/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestApiExploits />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/threat_model/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestApiThreatModel />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/report/pentest/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ReportsPageAPIPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/report/attestation/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<AttestationLetterAPIPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/api/report/executive/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ExecutiveReportAPIPentest />}
              />
            }
          />

          {/* Web Pentest Routes */}
          <Route
            exact
            path="/pentest/create/web"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<CreateWebPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/dashboard/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<WebPentestDashboard />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/scope/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<WebPentestScope />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/assets/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestWebAssets />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/exploits/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestWebExploits />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/threat_model/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<PentestWebThreatModel />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/report/pentest/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ReportsPageWebPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/report/attestation/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<AttestationLetterWebPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/report/executive/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ExecutiveReportWebPentest />}
              />
            }
          />
          <Route
            exact
            path="/pentest/web/report/recon/:pentest_id"
            element={
              <ProtectedRoute
                membership={membership}
                userEmail={userEmail}
                onMembershipUpdated={() => setMembership('beta')}
                element={<ReconReportWebPentest />}
              />
            }
          />
         


          {/* Account and team settings (allowed even without membership) */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="account" replace />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="team" element={<Team embedded />} />
          </Route>
          <Route path="/team" element={<Navigate to="/settings/team" replace />} />
          <Route path="/billing" element={<BillingPage />} />
          
          </Routes>
        </main>
      </div>
    </CreditsProvider>
  );
}

export default App;
